import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import type { AnalystTurnEvent } from '../../api/types';
import { color, elevation, fontSize, radius } from '../../theme/tokens';
import { CopyControl } from './CopyControl';
import { ResultTable } from './ResultTable';
import { STARTER_CHIPS } from './starters';
import { TurnDetails } from './TurnDetails';
import { TurnFooter, type ProvenanceSource } from './TurnFooter';
import type { ColumnSpec } from './format';

/**
 * The analyst modal (spec 006 T108/T109/T113/T114).
 *
 * ## ⚠ Composed from `<CommandPalette>`'s primitives, not invented
 *
 * Scrim, radius, elevation and click-outside are that component's, because Principle I builds a
 * non-blueprint surface out of blueprint parts rather than introducing a second visual language —
 * exactly as the export dialog does.
 *
 * ## ⚠ It renders through a PORTAL, and that is what makes the scrim a scrim
 *
 * `<AnalystButton>` sits in the page header's `actions` slot, and that header carries
 * `backdrop-filter: blur(10px)`. A non-`none` `backdrop-filter` (like `filter` and `transform`)
 * makes the element a **containing block for fixed descendants** — so `position: fixed; inset: 0`
 * resolved to the header's own box, a ~70px strip across the top of the window, and the whole
 * overlay was laid out and clipped inside it. Click-outside was not broken code: there was no
 * outside to click, because the scrim covered the header and nothing else.
 *
 * ⚠ Do not "simplify" this back to an inline render because the markup looks self-contained. The
 * dependency is on an ancestor three components away, in a file that has no idea this exists —
 * which is exactly why the mounting point is not something this component may rely on.
 * `<CommandPalette>` needs no portal only because it is mounted in the route shell, outside that
 * header.
 *
 * ## Three ways out, and they are not redundant
 *
 * The close button, the scrim and Escape. A reader mid-answer reaches for a different one of the
 * three, and this is the one surface in the product that can sit open for a minute while a
 * warehouse query runs — every exit also aborts the turn (see below).
 *
 * ## ⚠ ONE live region, and the token stream renders OUTSIDE it
 *
 * `role="status"` carries stage changes and the terminal outcome only. A live region fed
 * token-by-token is unusable: a screen reader re-announces on every mutation, so streaming prose
 * into one turns an answer into an unbroken stutter.
 */

type Turn = {
  question: string;
  stage: string | null;
  prose: string;
  columns: ColumnSpec[];
  rows: unknown[][];
  total: number;
  clock: string | null;
  reconciliation: string | null;
  assumptions: string[];
  provenance: string[];
  sql: string | null;
  tier: 0 | 1 | 2 | null;
  outcome: 'streaming' | 'answered' | 'clarified' | 'refused' | 'failed' | 'cancelled';
  message: string | null;
  /**
   * What a clarification offers. ⚠ Each one is a REPLY: the server holds the question it asked
   * and completes the original from the choice, so a button that opened a new thread, or sent the
   * option with the original question pasted in front of it, would answer something else.
   */
  options: string[];
  /** `null` until the answer lands, and when the turn produced no stored rows. */
  resultId: string | null;
  /**
   * Which row is the in-progress bucket (FR-A21).
   *
   * ⚠ Carried from the result's own `is_partial` column rather than assumed to be the last row:
   * a series is ordered newest-first by the compiler, and guessing would mark the wrong bucket
   * the day that ordering changes.
   */
  partialRowIndex: number | null;
};

const EMPTY_TURN = (question: string): Turn => ({
  question,
  stage: null,
  prose: '',
  columns: [],
  rows: [],
  total: 0,
  clock: null,
  reconciliation: null,
  assumptions: [],
  provenance: [],
  sql: null,
  tier: null,
  outcome: 'streaming',
  options: [],
  message: null,
  resultId: null,
  partialRowIndex: null,
});

export interface AnalystModalProps {
  open: boolean;
  onClose: () => void;
}

export function AnalystModal({ open, onClose }: AnalystModalProps) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  /** ⚠ One in-flight turn. Asking again cancels rather than queueing. */
  const abortRef = useRef<AbortController | null>(null);
  const threadRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  /*
   * ⚠ Closing the modal cancels the turn — the same mechanism as asking again and as navigating
   * away, because they are the same event: nobody is waiting for this answer any more. The abort
   * is what lets the server cancel the warehouse query, and a scan nobody will read is still
   * billed.
   */
  useEffect(() => {
    if (open) return;
    abortRef.current?.abort();
    abortRef.current = null;
  }, [open]);

  /*
   * Escape dismisses, the same listener idiom `<CommandPalette>` and the export dialog use. It is
   * on `window` rather than the panel because focus is not guaranteed to be inside it — the
   * transcript scrolls, a reader can click a row, and a dialog that only answers Escape while its
   * input is focused is a dialog that sometimes will not close.
   */
  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const ask = useCallback(
    async (text: string) => {
      if (!text.trim()) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setBusy(true);
      setQuestion('');
      setTurns((prior) => [...prior, EMPTY_TURN(text)]);

      const patch = (update: (turn: Turn) => Turn) =>
        setTurns((prior) => prior.map((t, i) => (i === prior.length - 1 ? update(t) : t)));

      try {
        await api.analystTurn(
          {
            question: text,
            ...(threadRef.current ? { threadId: threadRef.current } : {}),
          },
          (event: AnalystTurnEvent) => {
            switch (event.t) {
              case 'thread':
                // ⚠ What makes the next turn a follow-up (or a reply) instead of a new thread.
                threadRef.current = event.threadId;
                break;
              case 'stage':
                patch((t) => ({ ...t, stage: event.stage }));
                break;
              case 'rows': {
                // ⚠ Rows land before prose, and the total is the query's, not the running count.
                const columns = event.columns as unknown as ColumnSpec[];
                /*
                 * ⚠ The in-progress bucket is found from the result's own `is_partial` column.
                 * A period sum dives at the right edge for no reason but the period being days
                 * old; the chart dashes it, and a table that drew it like any other bucket would
                 * be the two readings of one snapshot disagreeing about the one thing they must
                 * agree on.
                 */
                const partialAt = columns.findIndex((c) => c.key === 'is_partial');
                patch((t) => {
                  const rows = [...t.rows, ...event.rows];
                  const partialRowIndex =
                    partialAt === -1 ? t.partialRowIndex : rows.findIndex((r) => r[partialAt] === true);
                  return {
                    ...t,
                    columns,
                    rows,
                    total: event.total,
                    partialRowIndex: partialRowIndex === -1 ? null : partialRowIndex,
                  };
                });
                break;
              }
              case 'delta':
                patch((t) => ({ ...t, prose: t.prose + event.text }));
                break;
              case 'answer':
                patch((t) => ({
                  ...t,
                  stage: null,
                  outcome: 'answered',
                  total: event.total,
                  clock:
                    event.clock.mode === 'live'
                      ? `Live — read at ${new Date(event.clock.readAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`
                      : `As of the dashboard — ${event.clock.asOf}`,
                  // ⚠ Prints even when the two agree. Its absence means there is no twin, not
                  // that they matched.
                  reconciliation: event.reconciliation
                    ? `The dashboard shows ${event.reconciliation.figure.toLocaleString('en-US')}, as of ${event.reconciliation.asOf}.`
                    : null,
                  assumptions: event.assumptions,
                  provenance: event.provenance,
                  sql: event.sql,
                  tier: event.tier,
                  resultId: event.resultId,
                }));
                break;
              case 'clarify':
                patch((t) => ({
                  ...t,
                  stage: null,
                  outcome: 'clarified',
                  message: event.question,
                  options: event.options ?? [],
                }));
                break;
              case 'refuse':
                patch((t) => ({ ...t, stage: null, outcome: 'refused', message: event.detail }));
                break;
              case 'failed':
                patch((t) => ({ ...t, stage: null, outcome: 'failed', message: 'The turn did not complete.' }));
                break;
              case 'cancelled':
                patch((t) => ({ ...t, stage: null, outcome: 'cancelled', message: 'Cancelled.' }));
                break;
            }
          },
          controller.signal,
        );
      } catch {
        // An aborted fetch is the expected path when the reader asks again or closes the modal.
        if (!controller.signal.aborted) {
          patch((t) => ({ ...t, stage: null, outcome: 'failed', message: 'The turn did not complete.' }));
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setBusy(false);
      }
    },
    [],
  );

  if (!open) return null;

  const last = turns.at(-1);
  /** ⚠ ONE live region: stages and outcomes only. Never the token stream. */
  const statusText = last?.stage
    ? `${last.stage}…`
    : last && last.outcome !== 'streaming'
      ? (last.message ?? 'Answer ready.')
      : '';

  /*
   * ⚠ Portalled to `document.body` — see the note at the top of this file. The page header that
   * mounts the button is a containing block for fixed descendants, so rendering in place makes
   * `inset: 0` mean "the header", not "the window".
   */
  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: color.scrim,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '8vh',
        zIndex: 100,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Ask the analyst"
        onClick={(event) => event.stopPropagation()}
        style={{
          /*
           * ⚠ **820px was sized for a conversation and a row answer is now a RECORD** (owner
           * request 2026-09-14): 21 columns for a placement, 18 for a job, where the projection
           * used to be five or six. At 820px a reader scrolled sideways through most of the
           * answer, which is what "not handling properly" looked like.
           *
           * `1180px` is `layout.contentMinWidth` — the width the rest of the product already
           * commits to — so this panel is never wider than a page the reader has, and `94vw`
           * still governs on a laptop. ⚠ It is a CEILING, not a fixed width: the panel is a chat
           * first, and prose measured across 1,180px is unreadable, which is why the transcript
           * keeps its own narrower measure rather than filling this.
           */
          width: 'min(1180px, 94vw)',
          /*
           * ⚠ A FLOOR as well as a ceiling. Without a minimum the panel collapses to its content,
           * so an empty thread puts the composer directly under the starter chips and it then
           * jumps down the moment the first answer lands — the control a reader is aiming at
           * moving under their cursor.
           *
           * `min(…, 82vh)` rather than a bare 560px: on a short viewport the floor would exceed
           * the ceiling, and a panel taller than the window cannot be scrolled back to.
           */
          minHeight: 'min(560px, 82vh)',
          maxHeight: '82vh',
          background: color.surface,
          border: `1px solid ${color.borderStrong}`,
          borderRadius: radius['3xl'],
          boxShadow: elevation.palette,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            // ⚠ `flex: none`, or a long transcript squeezes the title away — the same rule
            // `<CommandPalette>` already applies to its own header row.
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '13px 16px',
            borderBottom: `1px solid ${color.borderSubtle}`,
            fontSize: fontSize.captionStrong,
            color: color.ink,
          }}
        >
          <span style={{ flex: 1 }}>Ask the analyst</span>

          {/*
            ⚠ The glyph is `aria-hidden` and the name is on the button: a bare `✕` is announced as
            a multiplication sign, and the label has to say what closes — this dialog can be open
            over four different pages.
          */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close the analyst"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              // A 28px target around a 14px glyph. Smaller reads as decoration on a panel this
              // wide, and is the control a reader reaches for first.
              width: 28,
              height: 28,
              padding: 0,
              background: 'transparent',
              border: 'none',
              borderRadius: radius.lg,
              color: color.inkMuted,
              cursor: 'pointer',
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        {/*
          ⚠ ONE live region, mounted and empty while idle rather than rendered on demand: a live
          region inserted at the moment it fills is not reliably announced. Stage changes and the
          terminal outcome only — the prose renders outside it.
        */}
        <div role="status" aria-live="polite" className="sr-only">
          {statusText}
        </div>

        {/*
          The only scrolling region. ⚠ `minHeight: 0` is load-bearing and easy to omit: a flex
          child's default `min-height: auto` refuses to shrink below its content, so `overflow:
          auto` never engages — the child grows past the container and the composer is pushed out
          of the panel instead of the transcript scrolling.
        */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}>
          {turns.length === 0 ? (
            <div>
              <p style={{ color: color.inkMuted, fontSize: fontSize.caption, marginTop: 0 }}>
                Ask about jobs, submissions, placements, candidates, accounts or people — across the
                whole book, not just this page.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {STARTER_CHIPS.map((chip) => (
                  <button
                    key={chip}
                    type="button"
                    onClick={() => void ask(chip)}
                    style={{
                      background: color.surfaceSubtle,
                      border: `1px solid ${color.border}`,
                      borderRadius: radius.lg,
                      padding: '6px 10px',
                      fontSize: fontSize.caption,
                      color: color.ink,
                      cursor: 'pointer',
                    }}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {turns.map((turn, index) => (
            <TurnView
              key={index}
              turn={turn}
              // ⚠ Only the LATEST turn's options answer anything: the server applies a reply to the
              // question it is waiting on now, and an older clarification is no longer pending.
              onChoose={index === turns.length - 1 && !busy ? (option) => void ask(option) : null}
            />
          ))}
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void ask(question);
          }}
          style={{
            // ⚠ Pinned: `flex: none` keeps the composer at the bottom whatever the transcript
            // does, so the input a reader is typing into never moves.
            flex: 'none',
            display: 'flex',
            gap: 8,
            padding: 12,
            borderTop: `1px solid ${color.borderSubtle}`,
          }}
        >
          <input
            ref={inputRef}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="How many target jobs are open in R1?"
            aria-label="Your question"
            style={{
              flex: 1,
              border: `1px solid ${color.border}`,
              borderRadius: radius.lg,
              padding: '8px 11px',
              fontSize: fontSize.body,
              color: color.ink,
              background: color.surface,
            }}
          />
          {/*
            ⚠ Never disabled while busy. Asking again CANCELS the turn in flight — a reader who
            can see from the first rows that they asked the wrong thing should be able to stop.
          */}
          <button
            type="submit"
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.lg,
              padding: '8px 14px',
              background: color.surfaceSubtle,
              color: color.ink,
              cursor: 'pointer',
              fontSize: fontSize.caption,
            }}
          >
            {busy ? 'Ask again' : 'Ask'}
          </button>
        </form>

        {/*
          ⚠ FR-A76e. A composer that feels private and is not is the kind of surprise discovered
          at the worst moment — saying so once costs a line and makes the oversight power
          legitimate rather than covert.
        */}
        <p
          style={{
            flex: 'none',
            margin: 0,
            padding: '0 12px 10px',
            fontSize: fontSize.caption,
            color: color.inkHint,
          }}
        >
          An administrator can open your conversations for support.
        </p>
      </div>
    </div>,
    document.body,
  );
}

/**
 * ⚠ **A measure for the PROSE, while the table keeps the whole panel** (2026-09-14).
 *
 * The panel widened to 1,180px when a row answer became the view's whole record — 21 columns for
 * a placement — and a sentence set across 1,180px runs to ~140 characters a line, which is past
 * anything anyone reads comfortably. So the two halves of a turn are measured differently on
 * purpose: the question and the narration cap here, the table does not.
 *
 * ⚠ It is a `maxWidth`, not a column: at 94vw on a laptop the panel is already narrower than
 * this and the cap does nothing, which is the correct behaviour rather than a special case.
 */
const PROSE_MEASURE = 760;

function TurnView({ turn, onChoose }: { turn: Turn; onChoose: ((option: string) => void) | null }) {
  return (
    <section style={{ marginBottom: 22 }}>
      <p
        style={{
          fontSize: fontSize.captionStrong,
          color: color.ink,
          margin: '0 0 8px',
          maxWidth: PROSE_MEASURE,
        }}
      >
        {turn.question}
      </p>

      {turn.stage ? (
        <p style={{ fontSize: fontSize.caption, color: color.inkMuted, maxWidth: PROSE_MEASURE }}>
          {turn.stage}…
        </p>
      ) : null}

      {turn.rows.length > 0 ? (
        <>
          <ResultTable
            columns={turn.columns}
            rows={turn.rows}
            totalRows={turn.total}
            {...(turn.partialRowIndex === null ? {} : { partialRowIndex: turn.partialRowIndex })}
          />
          {/* FR-A06's turn actions: copy as Markdown, copy as TSV, export to Sheets. */}
          <CopyControl
            columns={turn.columns}
            rows={turn.rows}
            totalRows={turn.total}
            prose={turn.prose}
            resultId={turn.resultId}
          />
        </>
      ) : null}

      {/* ⚠ Outside the live region: a token stream fed to one is unusable. */}
      {turn.prose ? (
        <p
          style={{
            fontSize: fontSize.body,
            color: color.ink,
            marginTop: 10,
            maxWidth: PROSE_MEASURE,
          }}
        >
          {turn.prose}
        </p>
      ) : null}

      {turn.message ? (
        <p style={{ fontSize: fontSize.caption, color: color.inkMuted, maxWidth: PROSE_MEASURE }}>
          {turn.message}
        </p>
      ) : null}

      {turn.outcome === 'clarified' && turn.options.length > 0 ? (
        <div role="group" aria-label="Choose an answer" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
          {turn.options.map((option) => (
            <button
              key={option}
              type="button"
              disabled={!onChoose}
              onClick={() => onChoose?.(option)}
              style={{
                background: color.surfaceSubtle,
                border: `1px solid ${color.border}`,
                borderRadius: radius.lg,
                padding: '6px 10px',
                fontSize: fontSize.caption,
                color: onChoose ? color.ink : color.inkMuted,
                cursor: onChoose ? 'pointer' : 'default',
              }}
            >
              {option}
            </button>
          ))}
        </div>
      ) : null}

      <TurnFooter
        sources={turn.provenance as ProvenanceSource[]}
        clock={turn.clock}
        reconciliation={turn.reconciliation}
      />

      {turn.outcome === 'answered' ? (
        <TurnDetails
          sql={turn.sql}
          tier={turn.tier}
          assumptions={turn.assumptions}
          authored={turn.tier === 2}
        />
      ) : null}
    </section>
  );
}
